// Canonical data layer — GTM-ready, no analytics platform installed.
// Canonical event names are language-independent; locale is attached to
// every event automatically.
// Events: page_view, cta_start_project, whatsapp_click, form_start,
// form_step_view, form_step_complete, form_submit, project_received,
// visit_website_click, language_change.
const localeFromPath = () => {
  if (typeof window === "undefined") return "en";
  // Language locales + Africa Wave 1 country-market prefixes (e.g. /ma/…).
  const m = window.location.pathname.match(/^\/(pt|es|fr|de|ar|br|it|za|ma|eg|dz|mu|ke|ao|bw)(\/|$)/);
  return m ? m[1] : "en";
};

export const track = (event, data = {}) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, locale: localeFromPath(), ...data, ts: Date.now() });
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[track]", event, data);
  }
};

// Attribution context available at event time.
//
// First-party, first-touch persistence (sessionStorage): URL parameters are
// captured the moment the visitor lands and survive multi-step navigation,
// language switches and in-tab refreshes. A valid stored value is NEVER
// overwritten by a later blank/missing parameter. Consent-compatible: no
// cookies, no third-party trackers, per-tab session scope only.
const ATTR_KEY = "yanre_attr_v1";
// payload field -> URL parameter
const ATTR_PARAMS = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  fbclid: "fbclid",
  campaignVariant: "v",
  searchIntent: "intent",
};

const readStoredAttr = () => {
  try { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || "{}"); } catch { return {}; }
};

export const attribution = () => {
  if (typeof window === "undefined") return {};
  const q = new URLSearchParams(window.location.search);
  const stored = readStoredAttr();
  const out = {};
  let dirty = false;
  for (const [field, param] of Object.entries(ATTR_PARAMS)) {
    const fromUrl = q.get(param);
    if (fromUrl) {
      out[field] = fromUrl;
      if (stored[field] !== fromUrl) { stored[field] = fromUrl; dirty = true; }
    } else {
      out[field] = stored[field] || null; // never blank over a stored value
    }
  }
  if (dirty) {
    try { sessionStorage.setItem(ATTR_KEY, JSON.stringify(stored)); } catch { /* storage full/blocked */ }
  }
  if (!out.campaignVariant) out.campaignVariant = "A";
  return { language: localeFromPath(), ...out };
};

// Meta click identifiers (_fbc/_fbp cookies, set by the Meta Pixel when it
// runs under advertising consent). Unhashed per Meta spec; may be null.
const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

export const metaClickIds = () => ({ fbc: readCookie("_fbc"), fbp: readCookie("_fbp") });

// project_received (Google Ads PRIMARY conversion) — permanent fix from the
// 2026-09 forensic audit. Fires AT MOST ONCE per genuine, backend-persisted
// submission ID. The backend confirms persistence explicitly
// (persisted:true + submissionId); a mocked/intercepted HTTP 200 without
// that confirmation never reaches this function. Fired IDs persist in
// localStorage so refreshes, re-renders, back/forward navigation, retries
// and duplicate callbacks can never double-count a conversion.
const PR_KEY = "yanre_pr_fired_v1";

export const fireProjectReceived = (submissionId, data = {}) => {
  if (typeof window === "undefined" || !submissionId) return false;
  let fired = [];
  try { fired = JSON.parse(localStorage.getItem(PR_KEY) || "[]"); } catch { fired = []; }
  if (fired.includes(submissionId)) return false;
  track("project_received", { ...data, leadId: submissionId, event_id: submissionId });
  try { localStorage.setItem(PR_KEY, JSON.stringify([...fired, submissionId].slice(-50))); } catch { /* storage full/blocked */ }
  return true;
};
